import React, { useContext, useMemo, useState } from "react";
import { Image, View, StyleSheet } from "react-native";
import { useTheme } from "../hooks/use-theme";
import { useCurrencyFormatter, useI18n } from "../hooks/i18n-hooks";
import { capitalizeFirstLetter } from "../utils";
import { addMonths, format, isBefore } from "date-fns";
import {
    EarningType,
    Mission,
    ProfileType,
    RepetitionType,
    SalaryForecast,
} from "../__generated__/graphql";
import { GET_SALARY_FORECAST } from "../graphql/salaries";
import { useQuery } from "@apollo/client";
import Carousel from "../components/Carousel";
import Loading from "./Loading";
import Text from "./Text";
import { useIntercom } from "../hooks/intercom";
import InfoButton from "./InfoButton";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { STANDARD_WORKING_TIME_CLASSIC_SPEC } from "./working_time/helpers";
import { useDateFormatter } from "../hooks/date";
import Gap from "../ui_atoms/Gap";
import LabelValueText from "./LabelValueText";

type SalaryProjectionsProps = {
    mission: Mission;
    initialDisplayDate?: string;
};

type ProjectCardProps = {
    salaryForecast?: SalaryForecast;
    hasVariableFees: boolean;
    forecastMonthYear?: Date;
    onCardHeight?: (height: number) => void;
    isClient?: boolean;
};

const ProjectCard: React.FC<ProjectCardProps> = ({
    salaryForecast,
    hasVariableFees,
    forecastMonthYear,
    onCardHeight,
    isClient,
}) => {
    const styles = useStyles();
    const i18n = useI18n();
    const intlFormat = useDateFormatter();
    const formatCurrency = useCurrencyFormatter();

    return (
        <View
            style={styles.card}
            onLayout={(e) => {
                onCardHeight?.(e.nativeEvent.layout.height);
            }}
        >
            <View style={styles.cardBackground}>
                <Image
                    resizeMode="cover"
                    style={styles.image}
                    source={require("../assets/images/card-background.png")}
                />
            </View>
            {salaryForecast && forecastMonthYear && (
                <>
                    <Text style={styles.cardTitle}>
                        {capitalizeFirstLetter(
                            intlFormat(forecastMonthYear, {
                                year: "numeric",
                                month: "short",
                            }),
                        )}
                    </Text>
                    {isClient ? (
                        <>
                            <View style={styles.cardRow}>
                                <Text style={styles.cardRowLabel} allowFontScaling={false}>
                                    {i18n.t("salary_projection.total_invoiced")}
                                </Text>
                                <Text style={styles.cardRowValue} allowFontScaling={false}>
                                    {salaryForecast.invoiceSalary
                                        ? formatCurrency({
                                              amountInCents: salaryForecast.invoiceSalary,
                                          })
                                        : ""}
                                </Text>
                            </View>
                            {salaryForecast?.includedFeesAmount !== 0 && (
                                <LabelValueText
                                    textStyle={styles.cardDescription}
                                    label={`${i18n.t("salary_projection.client_fees")}${hasVariableFees ? " *" : ""}`}
                                    value={formatCurrency({
                                        amountInCents: salaryForecast.includedFeesAmount,
                                    })}
                                />
                            )}
                            {salaryForecast?.includedDeductionsAmount !== 0 && (
                                <LabelValueText
                                    textStyle={styles.cardDescription}
                                    label={i18n.t("salary_projection.client_deduction")}
                                    value={formatCurrency({
                                        amountInCents: salaryForecast.includedDeductionsAmount,
                                    })}
                                />
                            )}
                            {salaryForecast?.includedClientDeductionsAmount !== 0 && (
                                <LabelValueText
                                    textStyle={styles.cardDescription}
                                    label={i18n.t("salary_projection.client_payroll_taxes")}
                                    value={formatCurrency({
                                        amountInCents:
                                            salaryForecast.includedClientDeductionsAmount,
                                    })}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <View style={styles.cardRow}>
                                <Text style={styles.cardRowLabel} allowFontScaling={false}>
                                    {i18n.t("salary_projection.gross_salary")}
                                </Text>
                                <Text style={styles.cardRowValue} allowFontScaling={false}>
                                    {salaryForecast.grossSalary
                                        ? formatCurrency({
                                              amountInCents: salaryForecast.grossSalary,
                                          })
                                        : ""}
                                </Text>
                            </View>
                            {salaryForecast?.includedFeesAmount !== 0 && (
                                <LabelValueText
                                    textStyle={styles.cardDescription}
                                    label={`${i18n.t("salary_projection.worker_fees")}${hasVariableFees ? " *" : ""}`}
                                    value={formatCurrency({
                                        amountInCents: salaryForecast.includedFeesAmount,
                                    })}
                                />
                            )}
                            <View style={styles.cardRow}>
                                <Text style={styles.cardRowLabel} allowFontScaling={false}>
                                    {i18n.t("salary_projection.net_salary")}
                                </Text>
                                <Text style={styles.cardRowValue} allowFontScaling={false}>
                                    {salaryForecast.netSalary
                                        ? formatCurrency({
                                              amountInCents: salaryForecast.netSalary,
                                          })
                                        : ""}
                                </Text>
                            </View>
                            {salaryForecast?.includedDeductionsAmount !== 0 && (
                                <LabelValueText
                                    textStyle={styles.cardDescription}
                                    label={i18n.t("salary_projection.worker_deduction")}
                                    value={formatCurrency({
                                        amountInCents: salaryForecast.includedDeductionsAmount,
                                    })}
                                />
                            )}
                            {salaryForecast?.includedWorkerDeductionsAmount !== 0 && (
                                <LabelValueText
                                    textStyle={styles.cardDescription}
                                    label={i18n.t("salary_projection.worker_payroll_taxes")}
                                    value={formatCurrency({
                                        amountInCents:
                                            salaryForecast.includedWorkerDeductionsAmount,
                                    })}
                                />
                            )}
                        </>
                    )}
                    {hasVariableFees && (
                        <>
                            <Gap gap="2/3" />
                            <Text style={styles.cardDescription} allowFontScaling={false}>
                                *{i18n.t("salary_projection.footer_variable_fees_not_included")}
                            </Text>
                        </>
                    )}
                </>
            )}
        </View>
    );
};

const SalaryProjections = ({ mission, initialDisplayDate }: SalaryProjectionsProps) => {
    const { colors } = useTheme();
    const styles = useStyles();
    const i18n = useI18n();
    const { presentArticle } = useIntercom();
    const [cardHeight, setCardHeight] = useState(0);
    const { profileType } = useContext(ProfileSwitcherManagerContext);

    const currentMonthYear = useMemo(() => {
        const newDate = new Date();
        newDate.setDate(1);
        return newDate;
    }, []);

    const contractStartMonthYear = useMemo(() => {
        const contractStartDate = mission.currentContract?.startDate
            ? new Date(mission.currentContract.startDate)
            : new Date();
        contractStartDate.setDate(1);
        return contractStartDate;
    }, [mission.currentContract?.startDate]);

    const initialForecastMonthYear = useMemo(() => {
        const initialStartDate = initialDisplayDate
            ? new Date(initialDisplayDate)
            : contractStartMonthYear;

        if (isBefore(initialStartDate, currentMonthYear)) {
            return currentMonthYear;
        }
        return initialStartDate;
    }, [initialDisplayDate, currentMonthYear, contractStartMonthYear]);

    const contractEndDate = useMemo(() => {
        if (mission.currentContract?.effectiveEndDate) {
            return mission.currentContract.effectiveEndDate;
        }
        return mission.currentContract?.endDate;
    }, [mission.currentContract?.effectiveEndDate, mission.currentContract?.endDate]);

    const contractEndMonthYear = useMemo(() => {
        if (contractEndDate) {
            const endMonthYear = contractEndDate ? new Date(contractEndDate) : new Date();
            endMonthYear.setDate(1);
            return addMonths(endMonthYear, 1); // add 1 month to include the last month
        }
        return null;
    }, [contractEndDate]);

    const lastDate = useMemo(() => {
        const last = addMonths(new Date(initialForecastMonthYear), 6);

        if (contractEndMonthYear) {
            if (isBefore(contractEndMonthYear, last)) {
                return contractEndMonthYear;
            }
        }
        return last;
    }, [contractEndMonthYear, initialForecastMonthYear]);

    const nbMonths = useMemo(() => {
        // we always add 1 month as salary may be paid the next month if end is after de the 20 of the month
        // so we have at least 2 months
        let _nbMonths = 1;
        let curr = initialForecastMonthYear;
        do {
            _nbMonths += 1;
            curr = addMonths(new Date(curr), 1);
        } while (isBefore(curr, lastDate));
        return _nbMonths;
    }, [initialForecastMonthYear, lastDate]);

    const { loading, data: salaryForecastData } = useQuery(GET_SALARY_FORECAST, {
        variables: {
            monthYear: format(initialForecastMonthYear, "yyyy-MM-dd"),
            nbMonths,
            netSalary: mission.currentContract?.netSalary || 0,
            grossSalary: mission.currentContract?.grossSalary || 0,
            invoiceSalary: mission.currentContract?.invoiceSalary || 0,
            earningType: mission.currentContract?.earningType || EarningType.Hourly,
            contractStartDate:
                mission.currentContract?.startDate || format(currentMonthYear, "yyyy-MM-dd"),
            workingTimeClassicSpec: mission.currentContract?.workingTimeClassicSpec
                ? mission.currentContract?.workingTimeClassicSpec
                : STANDARD_WORKING_TIME_CLASSIC_SPEC,
            contractEndDate: mission.currentContract?.effectiveEndDate,
            hoursPerWeekFullTime: mission.currentContract?.hoursPerWeekFullTime || 0,
            workingAddressStateCode:
                mission.currentContract?.workingAddress?.locality.state.code || "",
            fees: mission.currentContract?.fees || [],
            deductions: mission.currentContract?.deductions || [],
        },
    });

    const hasVariableFees = useMemo(
        () =>
            mission.currentContract?.fees?.some((fee) => fee.repetition === RepetitionType.Count) ||
            false,
        [mission.currentContract?.fees],
    );

    return !loading ? (
        <>
            <Text style={styles.info} allowFontScaling={false}>
                {i18n.t("salary_projection.info")}
                <InfoButton onPress={() => void presentArticle("SalaryProjection")} />
            </Text>
            <Carousel
                loop={false}
                autoPlay={false}
                dotColor={colors.secondary}
                height={cardHeight}
                mode="parallax"
                maxItemWidth={500}
                modeConfig={{
                    parallaxScrollingScale: 0.9,
                    parallaxScrollingOffset: 50,
                }}
                scrollAnimationDuration={300}
                snapEnabled
                dotsOffset={0}
            >
                {(salaryForecastData?.getSalaryForecast || [])
                    .filter((value) => {
                        return value.grossSalary > 0;
                    })
                    .map((value, index) => (
                        <ProjectCard
                            key={value.monthYear}
                            salaryForecast={value}
                            hasVariableFees={hasVariableFees}
                            forecastMonthYear={new Date(value.monthYear)}
                            isClient={profileType === ProfileType.Client}
                            {...(index === 0 ? { onCardHeight: setCardHeight } : {})}
                        />
                    ))}
            </Carousel>
        </>
    ) : (
        <Loading />
    );
};

const useStyles = () => {
    const { marginSize, roundness, fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                info: {
                    ...fonts.bodyMedium,
                    color: colors.onSurfaceDisabled,
                    marginHorizontal: marginSize,
                    marginBottom: marginSize,
                },
                card: {
                    width: "100%",
                    minHeight: 200,
                    // maxWidth: 500,
                    flexDirection: "column",
                    borderRadius: roundness * 1.5,
                    paddingHorizontal: marginSize,
                    paddingVertical: marginSize,
                    backgroundColor: "white",
                    shadowColor: "black",
                    shadowOpacity: 0.6,
                    shadowRadius: 4,
                    elevation: 6,
                    shadowOffset: {
                        width: 0,
                        height: 1,
                    },
                },
                cardTitle: {
                    ...fonts.headlineMedium,
                    color: "white",
                    marginBottom: marginSize / 4,
                },
                cardRow: {
                    alignSelf: "stretch",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    marginTop: marginSize / 2,
                },
                cardRowLabel: {
                    flexGrow: 0,
                    flexShrink: 1,
                    justifyContent: "flex-start",
                    color: "white",
                    textAlign: "left",
                    ...fonts.labelMedium,
                },
                cardRowValue: {
                    flexGrow: 1,
                    flexShrink: 0,
                    justifyContent: "flex-end",
                    color: "white",
                    textAlign: "right",
                    ...fonts.bodyLarge,
                },
                cardDescription: {
                    ...fonts.bodySmall,
                    color: "#B1B1B1",
                    marginTop: 2,
                    marginBottom: 2,
                },
                cardBackground: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "darkblue",
                    borderRadius: roundness * 1.5,
                    overflow: "hidden",
                },
                image: {
                    width: "100%",
                    height: "100%",
                },
            }),
        [
            fonts.bodyMedium,
            fonts.headlineMedium,
            fonts.labelMedium,
            fonts.bodyLarge,
            fonts.bodySmall,
            colors.onSurfaceDisabled,
            marginSize,
            roundness,
        ],
    );
};

export default SalaryProjections;
